import { useEffect, useState } from "react";
import ThemeContext, { Theme } from "./theme-context";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

const isTheme = (value: string | null): value is Theme =>
  value === "dark" || value === "light" || value === "system";

export const resolvedTheme = (theme: Theme): "light" | "dark" => {
  switch (theme) {
    case "light":
    case "dark":
      return theme;
    case "system":
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
    default: {
      const exhaustive: never = theme;
      return exhaustive;
    }
  }
};

// Switching themes changes every colour token at once. Without this, elements
// with a colour transition fade over their own durations while the rest snap.
const withoutTransitions = (change: () => void) => {
  const style = document.createElement("style");
  style.textContent = "*,*::before,*::after{transition:none!important}";
  document.head.appendChild(style);
  change();
  // Flush the change, then let one frame paint before transitions come back.
  void window.getComputedStyle(document.body).opacity;
  requestAnimationFrame(() => requestAnimationFrame(() => style.remove()));
};

const applyThemeClass = (theme: Theme) => {
  const root = document.documentElement;
  const next = resolvedTheme(theme);
  if (root.classList.contains(next)) return;
  withoutTransitions(() => {
    root.classList.remove("light", "dark");
    root.classList.add(next);
  });
};

const ThemeProvider = ({
  children,
  defaultTheme = "system",
  storageKey = "vite-ui-theme",
  ...props
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey);
    return isTheme(stored) ? stored : defaultTheme;
  });

  useEffect(() => {
    applyThemeClass(theme);
    if (theme !== "system") return;

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyThemeClass("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  // Storage events fire in other tabs only, so this tab's own writes don't loop.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) return;
      // A null key means another tab cleared storage, theme included.
      if (event.key === null) setTheme(defaultTheme);
      else if (event.key === storageKey)
        setTheme(isTheme(event.newValue) ? event.newValue : defaultTheme);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [storageKey, defaultTheme]);

  const value = {
    theme,
    setTheme: (next: Theme) => {
      localStorage.setItem(storageKey, next);
      setTheme(next);
    },
  };

  return (
    <ThemeContext.Provider {...props} value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
